import { type UserPath } from './journeyFilter';

/**
 * User journey stage completion information
 */
export interface JourneyStageMetrics {
  count: number; // Whether the stage is completed (0 or 1)
  stageDuration: number; // Duration of current stage (from previous milestone to current milestone)
  cumulativeDuration: number; // Cumulative duration from journey start to current stage
  nwrs: string[]; // nwrs between previous milestone and current milestone, empty if not available
}

export interface JourneyStageAggregatedMetrics {
  name: string;
  conversion: number; // The conversion rate of the first step
  conversionPrev: number; // The conversion rate of the previous step
  avgDuration: number; // The funnel avg duration
  totalMatchedCount: number;
  totalStageDuration: number;
  totalCumulativeDuration: number;
  avgStageDuration: number;
  avgCumulativeDuration: number;
  totalPathCount: number;
  sessionCoverage: number;
  nwrs: string[]; // nwrs between previous milestone and current milestone, get from all paths and make unique, empty if not available
}

export function getJourneyStageAggregatedMetrics(
  userPaths: UserPath[],
  targetJourney: string[],
): JourneyStageAggregatedMetrics[] {
  if (userPaths.length === 0 || targetJourney.length === 0) {
    return [];
  }

  const totalPathCount = userPaths.length;

  // Get stage metrics for each user path
  const allPathsStageMetrics: JourneyStageMetrics[][] = userPaths.map((userPath) =>
    getJourneyStageSinglePathMetrics(userPath, targetJourney),
  );

  // Aggregate metrics across all user paths
  const aggregatedMetrics: JourneyStageAggregatedMetrics[] = [];

  let avgDuration = 0;

  for (let stageIndex = 0; stageIndex < targetJourney.length; stageIndex++) {
    // Step 1: Calculate number of users who matched this stage
    let totalMatchedCount = 0;
    let totalStageDuration = 0;
    let totalCumulativeDuration = 0;
    const aggregatedNwrsSet = new Set<string>();

    // Step 2: Iterate through all user paths, aggregate metrics for current stage
    for (const pathMetrics of allPathsStageMetrics) {
      if (stageIndex < pathMetrics.length) {
        const stageMetric = pathMetrics[stageIndex];

        // Count users who completed this stage
        if (stageMetric.count > 0) {
          totalMatchedCount++;
          totalStageDuration += stageMetric.stageDuration;
          totalCumulativeDuration += stageMetric.cumulativeDuration;

          // Collect unique nwrs from all paths for this stage
          if (stageMetric.nwrs && Array.isArray(stageMetric.nwrs)) {
            stageMetric.nwrs.forEach((nwr) => {
              if (nwr && nwr.trim()) {
                aggregatedNwrsSet.add(nwr.trim());
              }
            });
          }
        }
      }
    }

    // Step 3: Calculate averages
    const avgStageDuration = totalMatchedCount > 0 ? totalStageDuration / totalMatchedCount : 0;
    const avgCumulativeDuration =
      totalMatchedCount > 0 ? totalCumulativeDuration / totalMatchedCount : 0;

    // Step 4: Calculate session coverage (percentage of users who completed this stage)
    const sessionCoverage = totalPathCount > 0 ? (totalMatchedCount / totalPathCount) * 100 : 0;

    const conversion = stageIndex === 0 ? 1 : totalMatchedCount / totalPathCount;
    const conversionPrev =
      stageIndex === 0
        ? 1
        : totalMatchedCount / aggregatedMetrics[stageIndex - 1].totalMatchedCount;
    avgDuration += avgStageDuration;

    // Step 5: Build aggregated metrics
    aggregatedMetrics.push({
      name: targetJourney[stageIndex],
      conversion,
      conversionPrev,
      avgDuration,
      totalMatchedCount, // Total number of users who completed this stage
      totalStageDuration, // Total duration for this stage
      totalCumulativeDuration, // Total cumulative time to this stage
      avgStageDuration, // Average duration for this stage
      avgCumulativeDuration, // Average cumulative time to this stage
      totalPathCount, // Total number of user paths
      sessionCoverage, // Session coverage percentage
      nwrs: Array.from(aggregatedNwrsSet), // Unique nwrs from all paths for this stage
    });
  }

  return aggregatedMetrics;
}

/**
 * Calculate completion status for each stage of the journey in user path
 * @param userPath User path
 * @param targetJourney Target journey sequence
 * @returns Array of completion information for each stage
 */
export function getJourneyStageSinglePathMetrics(
  userPath: UserPath,
  targetJourney: string[],
): JourneyStageMetrics[] {
  if (targetJourney.length === 0 || userPath.length === 0) {
    return [];
  }

  // 1. Find positions of matched events
  const matchedPositions = findMatchedEventPositions(userPath, targetJourney);

  // 2. Calculate completion information for each stage
  return calculateAllStageMetrics(userPath, targetJourney, matchedPositions);
}

/**
 * Find positions of matched events in user path
 * @param userPath User path
 * @param targetJourney Target journey
 * @returns Array of matched event positions
 */
function findMatchedEventPositions(userPath: UserPath, targetJourney: string[]): number[] {
  const positions: number[] = [];
  let journeyIndex = 0;

  // Iterate through user path to find matching events
  for (
    let pathIndex = 0;
    pathIndex < userPath.length && journeyIndex < targetJourney.length;
    pathIndex++
  ) {
    const pathEvent = userPath[pathIndex].mapped_event_name;
    const targetEvent = targetJourney[journeyIndex];

    if (pathEvent === targetEvent) {
      positions.push(pathIndex);
      journeyIndex++;

      // Early return: if all events are found, no need to continue searching
      if (journeyIndex === targetJourney.length) {
        break;
      }
    }
  }

  return positions;
}

/**
 * Calculate completion information for all stages
 * @param userPath User path
 * @param targetJourney Target journey
 * @param matchedPositions Matched event positions
 * @returns Completion information for all stages
 */
function calculateAllStageMetrics(
  userPath: UserPath,
  targetJourney: string[],
  matchedPositions: number[],
): JourneyStageMetrics[] {
  const result: JourneyStageMetrics[] = [];
  let cumulativeDuration = 0;

  for (let stageIndex = 1; stageIndex <= targetJourney.length; stageIndex++) {
    if (stageIndex <= matchedPositions.length) {
      // Stage completed
      const stageDuration = calculateStageDuration(userPath, matchedPositions, stageIndex);
      cumulativeDuration += stageDuration;

      const stageNwrs = calculateStageNwrs(userPath, matchedPositions, stageIndex);

      result.push({
        count: 1,
        stageDuration,
        cumulativeDuration,
        nwrs: stageNwrs,
      });
    } else {
      // Stage not completed
      result.push({
        count: 0,
        stageDuration: 0,
        cumulativeDuration: 0,
        nwrs: [],
      });
    }
  }

  return result;
}

/**
 * Calculate stage duration
 * @param userPath User path
 * @param matchedPositions Matched event positions
 * @param stageIndex Stage index
 * @returns Stage duration
 */
function calculateStageDuration(
  userPath: UserPath,
  matchedPositions: number[],
  stageIndex: number,
): number {
  // First stage (single event) has duration of 0
  if (stageIndex === 1) {
    return 0;
  }

  // Calculate duration from previous event to current event (excluding start event)
  const startPos = matchedPositions[stageIndex - 2]; // Previous event position
  const endPos = matchedPositions[stageIndex - 1]; // Current event position

  let totalDuration = 0;
  for (let i = startPos + 1; i <= endPos; i++) {
    totalDuration += userPath[i].duration;
  }

  return totalDuration;
}

/**
 * Calculate stage nwrs (network requests between milestones)
 * @param userPath User path
 * @param matchedPositions Matched event positions
 * @param stageIndex Stage index
 * @returns Array of unique nwrs for this stage
 */
function calculateStageNwrs(
  userPath: UserPath,
  matchedPositions: number[],
  stageIndex: number,
): string[] {
  // First stage (single event) has no nwrs between milestones
  if (stageIndex === 1) {
    return [];
  }

  // Collect nwrs from previous event to current event (excluding start event)
  const startPos = matchedPositions[stageIndex - 2]; // Previous event position
  const endPos = matchedPositions[stageIndex - 1]; // Current event position

  const nwrsSet = new Set<string>();

  for (let i = startPos + 1; i <= endPos; i++) {
    const event = userPath[i];
    if (event.nwrs && Array.isArray(event.nwrs)) {
      event.nwrs.forEach((nwr) => {
        if (nwr && nwr.trim()) {
          nwrsSet.add(nwr.trim());
        }
      });
    }
  }

  return Array.from(nwrsSet);
}
